
// src/lib/actions/calendarSetup.js
import { supabase } from "../../supabaseClient";

async function signedInUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!user) throw new Error("You must be signed in to use your financial calendar.");
  return user;
}

export async function getMyFinancialCalendarProfile() {
  const user = await signedInUser();
  const { data, error } = await supabase.from("user_financial_calendar_profiles")
    .select("*").eq("user_id", user.id).maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function getMyCalendarSetupPrefill() {
  const user = await signedInUser();
  const { data, error } = await supabase.from("business_profiles")
    .select("legal_nature, registration_number, cipc_registered, registration_status, employee_count_range, bank_accounts_separate")
    .eq("user_id", user.id).maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function getMyFinancialCalendarQuestionnaire() {
  const user = await signedInUser();
  const { data, error } = await supabase
    .from("user_financial_calendar_questionnaires")
    .select("registration_month, financial_calendar, tax_type, vat_registered, vat_due_timing, paye_registered, uif_registered, updated_at")
    .eq("user_id", user.id).maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function saveMyFinancialCalendarQuestionnaire(values) {
  await signedInUser();
  const { data, error } = await supabase.rpc("save_my_financial_calendar_questionnaire", {
    p_answers: {
      registration_month: values.registration_month || null,
      financial_calendar: values.financial_calendar,
      tax_type: values.tax_type,
      vat_registered: values.vat_registered,
      vat_due_timing: values.vat_registered ? values.vat_due_timing : null,
      paye_registered: values.paye_registered,
      uif_registered: values.uif_registered,
    },
  });
  if (error) throw error;
  return data;
}

// Legacy setup helper kept for existing callers; no legacy fields are used for QCAL7 eligibility.
export async function saveMyFinancialCalendarSetup(values) {
  await signedInUser();
  const payload = {
    p_entity_type: values.entity_type || null,
    p_cipc_registered: values.cipc_registered,
    p_cipc_registration_number: values.cipc_registered ? values.cipc_registration_number || null : null,
    p_incorporation_date: values.cipc_registered ? values.incorporation_date || null : null,
    p_financial_year_end_month: Number(values.financial_year_end_month),
    p_financial_year_end_day: Number(values.financial_year_end_day),
    p_business_bank_account: values.business_bank_account,
    p_business_sells_on_credit: values.business_sells_on_credit,
    p_preferred_finance_review_day: Number(values.preferred_finance_review_day),
    p_vat_registered: values.vat_registered,
    p_vat_period_cycle: values.vat_registered ? values.vat_period_cycle || null : null,
    p_vat_next_period_end: values.vat_registered ? values.vat_next_period_end || null : null,
    p_vat_filing_channel: values.vat_registered ? values.vat_filing_channel || null : null,
    p_turnover_tax_registered: values.turnover_tax_registered,
    p_business_is_provisional_taxpayer: values.business_is_provisional_taxpayer,
    p_has_employees: values.has_employees,
    p_first_employee_start_date: values.has_employees ? values.first_employee_start_date || null : null,
    p_paye_registered: values.has_employees ? values.paye_registered : false,
    p_uif_registered: values.has_employees ? values.uif_registered : false,
    p_sdl_registered: values.has_employees ? values.sdl_registered : false,
    p_expected_12m_remuneration_band: values.has_employees ? values.expected_12m_remuneration_band || null : null,
    p_compensation_fund_registered: values.has_employees ? values.compensation_fund_registered : false,
    p_default_compliance_owner: values.default_compliance_owner || null,
    p_personal_tax_reminders: values.personal_tax_reminders,
    p_personal_tax_type: values.personal_tax_reminders ? values.personal_tax_type || null : null,
  };
  const { data, error } = await supabase.rpc("save_my_financial_calendar_setup", payload);
  if (error) throw error;
  return data;
}

export async function refreshMyFinancialActions() {
  const { data, error } = await supabase.rpc("refresh_my_questionnaire_calendar_actions");
  if (error) {
    console.error("Could not generate questionnaire calendar actions:", error);
    throw error;
  }
  return Number(data || 0);
}

export async function getMyActions() {
  const { data, error } = await supabase.rpc("get_my_actions");
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

function saDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Johannesburg", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

// Legacy and QCAL7 keys use different prefixes. Normalize before checking applicability.
function calendarActionCode(item) {
  const key = String(item.source_key || "").toUpperCase();
  return key.startsWith("QCAL7:") ? key.slice("QCAL7:".length).split(":")[0] : key.split(":")[0];
}

function isOpenAction(item) {
  return ["upcoming", "due", "overdue"].includes(item.status);
}

function isApplicableOpenAction(item, questionnaire, legalNature) {
  if (!questionnaire || !isOpenAction(item)) return true;
  const code = calendarActionCode(item);
  const key = String(item.source_key || "").toUpperCase();
  const isQcal7 = key.startsWith("QCAL7:");
  const entity = String(legalNature || "").trim().toLowerCase();
  const companyOrCC = ["company", "private_company", "private company", "pty ltd", "close corporation", "close_corporation", "cc"].includes(entity);

  if (code === "TAX_VAT201" || code.startsWith("TAX_VAT")) return questionnaire.vat_registered === true;
  if (code === "TAX_EMP201" || code.startsWith("TAX_EMP501") || code.startsWith("TAX_EMP")) return questionnaire.paye_registered === true;
  if (code === "TAX_UIF") return questionnaire.uif_registered === true;

  if (code === "TAX_TURNOVER" || code.startsWith("TAX_TT02") || code === "TAX_TT03" || code === "QCAL_TT_YEAR_VERIFY") {
    return questionnaire.tax_type === "turnover";
  }
  if (code === "TAX_PROVISIONAL" || code.startsWith("TAX_IRP6") || code === "QCAL_PROVISIONAL_VERIFY") {
    return questionnaire.tax_type === "provisional";
  }
  if (code === "QCAL_TAX_VERIFY") return questionnaire.tax_type === "unknown";
  if (code === "COMPLIANCE_CIPC_ANNUAL_RETURN" && isQcal7) {
    return companyOrCC && Number(questionnaire.registration_month) >= 1 && Number(questionnaire.registration_month) <= 12;
  }
  if (code === "QCAL_FIN_YEAR_VERIFY") return questionnaire.financial_calendar === "Jun - Jul";
  if (code === "QCAL_FIN_YEAR_REVIEW" && isQcal7) {
    return ["Mar - Feb", "Jan - Dec"].includes(questionnaire.financial_calendar);
  }
  return true;
}

export async function getMyCalendarReminders() {
  const user = await signedInUser();
  const { data: profile, error: profileError } = await supabase
    .from("user_financial_calendar_profiles").select("setup_completed_at")
    .eq("user_id", user.id).maybeSingle();
  if (profileError) throw profileError;
  const calendarStartDate = saDate(profile?.setup_completed_at);
  if (!calendarStartDate) return [];

  const [{ data: questionnaire, error: questionnaireError }, { data: business, error: businessError }] = await Promise.all([
    supabase.from("user_financial_calendar_questionnaires")
      .select("registration_month, financial_calendar, tax_type, vat_registered, paye_registered, uif_registered")
      .eq("user_id", user.id).maybeSingle(),
    supabase.from("business_profiles").select("legal_nature")
      .eq("user_id", user.id).maybeSingle(),
  ]);
  if (questionnaireError) throw questionnaireError;
  if (businessError) throw businessError;

  const { data, error } = await supabase.from("user_action_occurrences")
    .select("id, source_key, title, description, category, action_type, due_at, available_from, status, source_type, metadata")
    .eq("user_id", user.id).not("due_at", "is", null)
    .order("due_at", { ascending: true });
  if (error) throw error;
  return (data || []).filter((item) => {
    if (["dismissed", "not_applicable"].includes(item.status)) return false;
    if (item.source_key === "SYSTEM_CALENDAR_SETUP" || item.action_type === "calendar_setup") return false;
    const day = saDate(item.due_at);
    if (!day || day < calendarStartDate) return false;
    // Historical completions remain visible even if the questionnaire subsequently changes.
    return isApplicableOpenAction(item, questionnaire, business?.legal_nature);
  });
}

export async function completeMyActionOccurrence(occurrenceId) {
  if (!occurrenceId) throw new Error("An action occurrence ID is required.");
  const { data, error } = await supabase.rpc("complete_my_action_occurrence", {
    p_occurrence_id: occurrenceId,
  });
  if (error) throw error;
  return data;
}

export async function getMyCompletedFinancialActions() {
  const { data, error } = await supabase.rpc("get_my_completed_financial_actions");
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
